import React from "react"
import { useTranslation } from "react-i18next"
import { useAlert } from "hooks/useAlert"
import FormElement from "components/Form/FormElement"
import { useModal } from "hooks/useModal"
import { useAuth } from "hooks/useAuth"
import * as PartnerApi from "features/Partner/api/PartnerApi"
import { 
    OCODE_GENDERS_LIST,
    ACTIVATED_ACCOUNT,
    SUSPENDED_ACCOUNT,
    DELETED_ACCOUNT 
} from "utils/constants"

const defaultFormEvents = {
    ready: true,
    editable: false
}

export const ReferenceForm = ({
    partner,
    setUpdatedPartner,
    errors,
    setErrors,
    refresh,
    showLocalErrors,
    setShowLocalErrors
}) => {
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

    const { t } = useTranslation()
    const { showModal } = useModal()
    const { user } = useAuth()
    const { alert } = useAlert()
    
    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setUpdatedPartner(s => ({
                ...s,
                [name]: value
            }))
        }, [setUpdatedPartner]
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const handleSubmit = async (event, targetedStatus) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            showModal(
                ModalConfirmation,
                {
                    body: () => (
                        <>
                            {targetedStatus === ACTIVATED_ACCOUNT && // activated account
                                <>
                                    <h2 className="c-h2 font-bold text-primary text-center my-7">{t("store.activate.confirm_modal_title", { ns: "partner" })}</h2>
                                    <div className="u-text-color py-7 mb-7">
                                        <p className="text-center mb-2">
                                            {t("store.activate.confirm_modal_desc1", { ns: "partner" })} <br/>
                                            {t("store.activate.confirm_modal_desc2", { ns: "partner" })}
                                        </p>
                                    </div>
                                </>
                            }
                            {targetedStatus === SUSPENDED_ACCOUNT && // suspended account
                                <>
                                    <h2 className="c-h2 font-bold text-primary text-center my-7">{t("store.suspend.confirm_modal_title", { ns: "partner" })}</h2>
                                    <div className="u-text-color py-7 mb-7">
                                        <p className="text-center mb-2">
                                            {t("store.suspend.confirm_modal_desc1", { ns: "partner" })} <br/>
                                            {t("store.suspend.confirm_modal_desc2", { ns: "partner" })}
                                        </p>
                                    </div>
                                </>
                            }
                            {targetedStatus === DELETED_ACCOUNT && // deleted account
                                <>
                                    <h2 className="c-h2 font-bold text-primary text-center my-7">{t("store.delete.confirm_modal_title", { ns: "partner" })}</h2>
                                    <div className="u-text-color py-7 mb-7">
                                        <p className="text-center mb-2">
                                            {t("store.delete.confirm_modal_desc1", { ns: "partner" })} <br/>
                                            {t("store.delete.confirm_modal_desc2", { ns: "partner" })}
                                        </p>
                                    </div>
                                </>
                            }
                        </>
                    ),
                    callback: () => {
                        ; (async () => {
                            // hide error
                            setShowLocalErrors(false)
                            // prevent form to be submitted again
                            setFormEvents(s => ({
                                ...s,
                                ready: false
                            }))

                            // TODO: is the synchronous call working ?
                            setUpdatedPartner(s => ({
                                ...s,
                                status: targetedStatus
                            }))

                            const newPartner = {
                                entity_id: partner.entity_id,
                                status: targetedStatus,
                                last_row_hash: partner.row_hash
                            }
                            if (OCODE_GENDERS_LIST.includes(user?.gender)) { // O°Code
                                const { getError } = await PartnerApi.updatePartnerAccount(newPartner)
                                if (getError()) throw getError()
                            } else {
                                //@ TODO autre route sinon car lié aux opérateurs
                            }

                            alert("success", t("edit_success", { ns: "partner" }))
                            await refresh()

                            setFormEvents(s => ({
                                ...s,
                                ready: true
                            }))
                        })()
                    }
                },
                { style: { maxWidth: "800px" } }
            )
        }
    }

    const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
        readonly: (parseInt(partner.status) !== 0) || !formEvents.editable
	}

    return (
        <form className="">
            <section className="c-card p-5 md:p-12 mb-7">
                <h2 className="c-h2 font-semibold mb-7 uppercase">
                    {partner.status === 0 && <>{t("edit.partner_ref", { ns: "partner" })}</>}
                    {partner.status === ACTIVATED_ACCOUNT && <>{t("edit.act_to", { ns: "partner" })} {partner.raison_sociale}</> }
                </h2>
                <div className="flex flex-wrap w-full">
                    <div className="lg:w-1/2 w-full">
                        {/* numéro de déclarateur */}
                        <FormElement
                            {...formElementCommonProps}
                            value={partner.declarator_number}
                            name="declarator_number"
                            type="text"
                            label={t("edit.declarator_number", { ns: "partner" })}
                            className="mb-12"
                        />

                        {/* Etat du référencement */}
                        <FormElement
                            {...formElementCommonProps}
                            value={partner.bicycode_state}
                            name="bicycode_state"
                            type="text"
                            label={t("edit.state_of_ref", { ns: "partner" })}
                        />
                    </div>

                    <div className="lg:w-1/2 w-full l-grid justify-end items-end pt-7">
                        {(OCODE_GENDERS_LIST.includes(user?.gender)) && user?.permission === 0 && partner.status === 2 &&
                            <div className="flex flex-col">
                                <button
                                    type="submit"
                                    className="c-btn uppercase c-btn--primary px-7 mb-7"
                                    onClick={(e) => handleSubmit(e, ACTIVATED_ACCOUNT)}
                                    style={{ alignSelf: "justify-end" }}
                                >
                                    {t("edit.activate", { ns: "partner" })}
                                </button>
                                <button
                                    type="submit"
                                    className="c-btn uppercase c-btn--secondary px-7"
                                    onClick={(e) => handleSubmit(e, DELETED_ACCOUNT)}
                                    style={{ alignSelf: "justify-end" }}
                                    disabled={true}
                                >
                                    {t("edit.delete", { ns: "partner" })}
                                </button>
                            </div>
                        }

                        {(OCODE_GENDERS_LIST.includes(user?.gender)) && user?.permission === 0 && partner.status === 1 &&
                            <button
                                type="submit"
                                className="c-btn uppercase c-btn--secondary px-7 "
                                onClick={(e) => handleSubmit(e, SUSPENDED_ACCOUNT)}
                            >
                                {t("edit.deactivate", { ns: "partner" })}
                            </button>
                        }
                    </div>
                </div>
            </section>
        </form>
    )
}

const ModalConfirmation = ({ body, callback }) => {
    const { t } = useTranslation()
    const { hideModal } = useModal()
    const validate = () => {
        hideModal()
        callback()
    }
    return (
        <div className="p-12">
            {React.createElement(body)}
            <div className="flex justify-between flex-col md:flex-row-reverse">
                <button
                    type="button"
                    className="c-btn c-btn--secondary px-12 mb-5 lg:m-0"
                    onClick={validate}
                >
                    {t("global.confirm")}
                </button>
                <button
                    type="button"
                    className="c-btn c-btn--white-bordered px-7 mb-5 lg:m-0"
                    onClick={hideModal}
                >
                    {t("global.cancel")}
                </button>
            </div>
        </div>
    )
}
